import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import {
  DataGrid,
  GridColumnProps,
  SelectFilterComponent,
  SelectFloatingFilterComponent,
  StandardDialog,
  StatusChip,
  StatusType,
  YesNoChip,
} from "@roc/ui";
import { makeStyles } from "@material-ui/core/styles";
import { formatDate } from "@roc/feature-utils";
import { useStore } from "@roc/client-portal-shared/hooks";
import { formatPhoneNumber } from "@roc/ui/utils";
import { ChatRoomDialog } from "@roc/feature-general-tasks";
import { Badge, Box, Grid } from "@material-ui/core";
import { ReactComponent as ChatBubble } from 'libs/feature-loan-details/src/icons/chatBubble.svg';

const useStyles = makeStyles((theme) => ({
  paginationBar: {
    border: "none",
    borderTop: "1px solid #eee",
    padding: 8,
    background: "#fafafa",
  },
  pageLayoutContainer: {
    padding: "2.5px",
  },
  gridContainer: {
    display: "flex",
    flexGrow: 1,
    flexDirection: "column",
    height: "65vh",
    overflow: "hidden",
  },
}));

export const BorrowerReferralsDataGrid = observer(() => {
  const classes = useStyles();
  const { referralsStore } = useStore();
  const { gridStore } = referralsStore;

  useEffect(() => {
    gridStore.resetAndFetchGridData();
  }, []);

  const onSortChanged = (params) => {
    const sortModel = params.api.getSortModel();
    gridStore.setSortModel(sortModel);
  };

  const onFilterChanged = (params) => {
    const filterModel = params.api.getFilterModel();
    gridStore.setFilterModel(filterModel);
  };

  const handleOpenDialog = (referrerBorrowerId) => {
    referralsStore.getConversationSidById(referrerBorrowerId);
  };

  const handleCloseDialog = () => {
    referralsStore.setChatDialogOpen(false);
    referralsStore.setCurrentConversationSid(null);
  };

  const columns: GridColumnProps[] = [
    {
      field: "referredBy",
      headerName: "Referrer",
      minWidth: 250,
      cellRenderer: "referrerCellRenderer",
      filter: "agTextColumnFilter",
      floatingFilter: true,
      floatingFilterComponentParams: { suppressFilterButton: true },
    },
    {
      field: "fullName",
      headerName: "Referral Name",
      minWidth: 240,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      floatingFilterComponentParams: { suppressFilterButton: true },
    },
    {
      field: "email",
      headerName: "Referral Email",
      minWidth: 240,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      floatingFilterComponentParams: { suppressFilterButton: true },
    },
    {
      field: "phone",
      headerName: "Referral Phone",
      minWidth: 160,
      cellRenderer: "phoneCellRenderer",
      filter: "agTextColumnFilter",
      floatingFilter: true,
      floatingFilterComponentParams: { suppressFilterButton: true },
    },
    {
      field: "submittedDate",
      headerName: "Referral Submitted Date",
      minWidth: 240,
      cellRenderer: "dateCellRenderer",
      filter: "agDateColumnFilter",
      floatingFilter: true,
      floatingFilterComponentParams: { suppressFilterButton: true },
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 118,
      cellRenderer: "statusCellRenderer",
      filter: "selectFilter",
      floatingFilter: true,
      floatingFilterComponent: "selectFloatingFilter",
      floatingFilterComponentParams: {
        suppressFilterButton: true,
        options: [
          { label: "Referred", value: "REFERRED" },
          { label: "Submitted", value: "SUBMITTED" },
          { label: "Funded", value: "FUNDED" },
          { label: "Loan No Longer in Process", value: "NO_LONGER_IN_PROGRESS" },
          { label: "Ineligible for Program", value: "INELIGIBLE" },
          { label: "Paid Out", value: "PAID_OUT" }
        ]
      }
    },
    {
      field: "lenderName",
      headerName: "Originator Name",
      minWidth: 190,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      floatingFilterComponentParams: { suppressFilterButton: true },
    },
    {
      field: "loanId",
      headerName: "Loan Id",
      minWidth: 150,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      floatingFilterComponentParams: { suppressFilterButton: true },
    },
    {
      field: "fundedDate",
      headerName: "Funded Date",
      minWidth: 200,
      cellRenderer: "dateCellRenderer",
      filter: "agDateColumnFilter",
      floatingFilter: true,
      floatingFilterComponentParams: { suppressFilterButton: true },
    },
    {
      field: "estimatedPaymentDate",
      headerName: "Estimated Payment Date",
      minWidth: 200,
      cellRenderer: "dateCellRenderer",
      filter: "agTextColumnFilter",
      floatingFilter: true,
      floatingFilterComponentParams: { suppressFilterButton: true },
    },
    {
      field: "achBankInfo",
      headerName: "ACH Bank Info",
      minWidth: 200,
      cellRenderer: "statusAchCellRenderer",
      filter: "selectFilter",
      floatingFilter: true,
      floatingFilterComponent: "selectFloatingFilter",
      floatingFilterComponentParams: {
        suppressFilterButton: true,
        options: [
          { label: "Yes", value: "Yes" },
          { label: "No", value: "No" }
        ]
      }
    }
  ];

  const frameworkComponents = {
    selectFilter: SelectFilterComponent,
    selectFloatingFilter: SelectFloatingFilterComponent,
    dateCellRenderer: ({ value }) => formatDate(value, "MM/dd/yyyy") ?? null,
    statusCellRenderer: ({ value }) =>
      value ? (
        <StatusChip
          statusType={StatusType.REFERRAL}
          label={value}
          variant={"filled"}
        />
      ) : null,
    statusAchCellRenderer: ({ value }) =>
      value ? <YesNoChip value={value} /> : null,
    phoneCellRenderer: ({ value }) => formatPhoneNumber(value),
    referrerCellRenderer: ({ value, data }) => (
      <div style={{ display: "flex", alignItems: "center" }}>
        <span style={{ marginRight: 15 }}>{value}</span>
        {data?.referrerBorrowerId && (
          <Badge
            badgeContent={data?.unReadCount > 0 ? data.unReadCount : 0}
            color="error"
            invisible={data?.unReadCount === 0}
            overlap="circular"
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            onClick={() => handleOpenDialog(data?.referrerBorrowerId)}
          >
            <ChatBubble onClick={() => handleOpenDialog(data?.referrerBorrowerId)}
              style={{ cursor: 'pointer', display: 'inline-block' }}
            />
          </Badge>
        )}
      </div>
    )
  };

  return (
  <Box>
    <Grid item className={classes.gridContainer}>
      <DataGrid
        columns={columns}
        rows={gridStore.gridData.data.rows}
        frameworkComponents={frameworkComponents}
        onSortChanged={onSortChanged}
        isLoading={gridStore.isLoading}
        sortModel={gridStore.sortModel}
        filterModel={gridStore.filterModel}
        paginationData={gridStore.gridData.meta}
        setPageNumber={gridStore.setPageNumber}
        onFilterChanged={onFilterChanged}
        showFilters={true}
        showPagination={true}
        rowHeight={45}
        enablePaginationSelectPage={true}
      />
      <StandardDialog
        open={referralsStore.chatDialogOpen}
        title="Chat Room"
        handleClose={handleCloseDialog}
        dialogContent={
          <div style={{ padding: 16 }}>
            {referralsStore.currentConversationSid ? (
              <ChatRoomDialog conversationSid={referralsStore.currentConversationSid} />
            ) : (
              <p>No chat room selected</p>
            )}
          </div>
        }
      />
    </Grid>
  </Box>
  );
});
